import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Box,
  TextField,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Paper,
  Typography,
  Divider,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  List,
  Select,
  MenuItem,
  InputLabel,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Collapse,
  Snackbar,
} from '@mui/material';
import {
  AutoAwesome,
  Close,
  CheckCircle,
  Add,
  Delete,
  Edit,
  CloudUpload,
  Refresh,
  Lightbulb,
  SaveAlt,
  Send,
  ArrowBack,
  ArrowForward,
  Description,
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { useAuth } from '../../contexts/AuthContext';
import MilestoneReportDocumentPreview from './MilestoneReportDocumentPreview';
import milestoneService from '../../services/milestoneService';
import DialogCloseButton from '../common/DialogCloseButton';
import { brand } from '../../theme/colors';

// Completion-report documents: plans and 3D designs as well as office files.
// Mirrors the design extensions the server accepts (FileUploadService).
const REPORT_DOCUMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*,'
  + '.skp,.layout,.dwg,.dxf,.rvt,.rfa,.pln,.3ds,.max,.obj,.stl,.fbx,.dae,.blend,.glb,.gltf,.ifc';

const MILESTONE_OPTIONS = [
  // Structure & Civil Works
  { group: 'Structure & Civil Works', label: 'General Building' },
  { group: 'Structure & Civil Works', label: 'Residential Construction' },
  { group: 'Structure & Civil Works', label: 'Commercial Construction' },
  { group: 'Structure & Civil Works', label: 'Foundation Work' },
  { group: 'Structure & Civil Works', label: 'Structural Engineering' },
  { group: 'Structure & Civil Works', label: 'Masonry & Brickwork' },
  { group: 'Structure & Civil Works', label: 'Concrete Work' },
  { group: 'Structure & Civil Works', label: 'Siting and Setting Out' },
  { group: 'Structure & Civil Works', label: 'Trenches & Excavation' },
  { group: 'Structure & Civil Works', label: 'Damp Proof Course (DPC) & Floor Slab' },
  // Roofing & Exterior
  { group: 'Roofing & Exterior', label: 'Roofing' },
  { group: 'Roofing & Exterior', label: 'Thatching' },
  { group: 'Roofing & Exterior', label: 'Guttering & Drainage' },
  { group: 'Roofing & Exterior', label: 'Waterproofing' },
  { group: 'Roofing & Exterior', label: 'Pressure Washing & Exterior Painting' },
  { group: 'Roofing & Exterior', label: 'Fence & Gate Repairs' },
  { group: 'Roofing & Exterior', label: 'Driveway & Walkway Cleaning' },
  // Mechanical, Electrical & Plumbing (MEP)
  { group: 'Mechanical, Electrical & Plumbing (MEP)', label: 'Plumbing Repairs & Fixture Replacements' },
  { group: 'Mechanical, Electrical & Plumbing (MEP)', label: 'Electrical Troubleshooting & Wiring Updates' },
  { group: 'Mechanical, Electrical & Plumbing (MEP)', label: 'HVAC & Ventilation' },
  { group: 'Mechanical, Electrical & Plumbing (MEP)', label: 'HVAC Tune-ups & Filter Replacements' },
  { group: 'Mechanical, Electrical & Plumbing (MEP)', label: 'Borehole Drilling' },
  { group: 'Mechanical, Electrical & Plumbing (MEP)', label: 'Solar Installation' },
  { group: 'Mechanical, Electrical & Plumbing (MEP)', label: 'Welding & Metalwork' },
  // Interior Finishes
  { group: 'Interior Finishes', label: 'Plastering & Rendering' },
  { group: 'Interior Finishes', label: 'Painting & Decorating' },
  { group: 'Interior Finishes', label: 'Drywall Repair and Painting' },
  { group: 'Interior Finishes', label: 'Tiling & Flooring' },
  { group: 'Interior Finishes', label: 'Flooring Repair & Replacement' },
  { group: 'Interior Finishes', label: 'Ceiling Installation' },
  { group: 'Interior Finishes', label: 'Carpentry & Joinery' },
  { group: 'Interior Finishes', label: 'Kitchen & Bathroom Fitting' },
  { group: 'Interior Finishes', label: 'Door & Window Maintenance' },
  { group: 'Interior Finishes', label: 'Appliance Installation & Maintenance' },
  // Outdoor & Grounds
  { group: 'Outdoor & Grounds', label: 'Landscaping & Lawn Care' },
  { group: 'Outdoor & Grounds', label: 'Tree Trimming & Debris Removal' },
  { group: 'Outdoor & Grounds', label: 'Swimming Pools' },
  { group: 'Outdoor & Grounds', label: 'Seasonal Landscaping & Leaf Removal' },
  { group: 'Outdoor & Grounds', label: 'Gutter Cleaning & Drainage Checks' },
  { group: 'Outdoor & Grounds', label: 'Snow & Ice Removal' },
  { group: 'Outdoor & Grounds', label: 'Pest Prevention & Treatment' },
  { group: 'Outdoor & Grounds', label: 'Winterization & Pipe Protection' },
  // Inspections & Compliance
  { group: 'Inspections & Compliance', label: 'Walling Inspection (Mid-construction)' },
  { group: 'Inspections & Compliance', label: 'Roof Structure Inspection (Roof Carcass)' },
  { group: 'Inspections & Compliance', label: 'Drainage & Sewerage (Drain Test)' },
  { group: 'Inspections & Compliance', label: 'Final Inspection (Occupancy Certificate)' },
  { group: 'Inspections & Compliance', label: 'Fire Alarm & Smoke Detector Testing' },
  { group: 'Inspections & Compliance', label: 'Safety & Code Compliance Checks' },
  { group: 'Inspections & Compliance', label: 'Plumbing & Electrical System Reviews' },
  { group: 'Inspections & Compliance', label: 'Equipment Calibration & Lubrication' },
  // Renovation & Remodeling
  { group: 'Renovation & Remodeling', label: 'Renovation & Remodeling' },
  { group: 'Renovation & Remodeling', label: 'Minor Remodeling or Painting Projects' },
  { group: 'Renovation & Remodeling', label: 'Interior Design' },
  { group: 'Renovation & Remodeling', label: 'Handyman Repairs & Light Carpentry' },
  // Professional Services
  { group: 'Professional Services', label: 'Project Management' },
  { group: 'Professional Services', label: 'Quantity Surveying' },
  { group: 'Professional Services', label: 'Architectural Services' },
  { group: 'Professional Services', label: 'Engineering Consulting' },
  { group: 'Professional Services', label: 'Environmental Impact Assessment (EIA)' },
  { group: 'Professional Services', label: 'NCA Registration Support' },
  // Cleaning & Property Services
  { group: 'Cleaning & Property Services', label: 'Deep Cleaning & Janitorial Services' },
  { group: 'Cleaning & Property Services', label: 'Trash Hauling & Property Turnover Cleaning' },
  { group: 'Cleaning & Property Services', label: 'Property Maintenance (General)' },
];

const AIMilestoneReportGenerator = ({ 
  open, 
  onClose, 
  projectId,
  projectContext = {},
  onReportCreated,
  reportMode = 'stage_completion',
  initialReport = null,   // an unsent draft to resume (full report incl. deliverables)
  onDraftDeleted,
}) => {
  const { getApiClient, user } = useAuth();
  // First-timer guidance
  const [showReportGuide, setShowReportGuide] = useState(
    !localStorage.getItem('eqaya_contractor_milestone_report_guide_seen')
  );

  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // The draft this dialog created — later saves update it instead of
  // creating another (auto-save used to leave a new draft every 5 s).
  const [draftId, setDraftId] = useState(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Report data
  const [milestoneName, setMilestoneName] = useState('');
  const [milestoneOverview, setMilestoneOverview] = useState('');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [deliverables, setDeliverables] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [acceptanceCriteria, setAcceptanceCriteria] = useState('');
  const [dependencies, setDependencies] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState('');
  const [durationUnit, setDurationUnit] = useState('weeks');
  const [referencePhotos, setReferencePhotos] = useState([]);
  const [referenceDocuments, setReferenceDocuments] = useState([]);
  
  // Payment/Cost fields
  const [laborCost, setLaborCost] = useState('');
  const [materialsCost, setMaterialsCost] = useState('');
  const [materialCostCoverage, setMaterialCostCoverage] = useState(''); // 'contractor', 'client', 'shared'
  const [materialCostCoverageDetails, setMaterialCostCoverageDetails] = useState('');
  const [otherCharges, setOtherCharges] = useState([]); // Array of {description, amount, category}
  const [currency, setCurrency] = useState('USD');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [handoverSummary, setHandoverSummary] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [newCertificate, setNewCertificate] = useState('');
  const [punchListItems, setPunchListItems] = useState([]);
  const [newPunchListItem, setNewPunchListItem] = useState('');
  const [contractorSignoff, setContractorSignoff] = useState(false);
  
  // UI state
  const [newExclusion, setNewExclusion] = useState('');
  const [newOtherCharge, setNewOtherCharge] = useState({ description: '', amount: '', category: 'other' });
  const [editingDeliverable, setEditingDeliverable] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [savedDraft, setSavedDraft] = useState(false);

  // Add deliverable dialog state
  const [addDeliverableDialogOpen, setAddDeliverableDialogOpen] = useState(false);
  const [newDeliverable, setNewDeliverable] = useState({
    name: '',
    type: 'physical',
    description: '',
    acceptanceCriteria: '',
    qualityStandard: '',
  });

  const isFinalHandover = reportMode === 'final_handover';

  const steps = [
    'Stage Name',
    'Overview & Scope',
    'Deliverables',
    'Exclusions & Criteria',
    'Timeline & Evidence',
    'Payment & Costs',
    isFinalHandover ? 'Final Handover' : 'Review & Submit'
  ];

  // Resume an unsent draft: load everything it had, and keep updating that
  // same row (draftId) instead of creating a new one.
  useEffect(() => {
    if (!open || !initialReport) return;
    const r = initialReport;
    const parse = (v, fallback) => {
      if (v === null || v === undefined || v === '') return fallback;
      if (typeof v !== 'string') return v;
      try { return JSON.parse(v); } catch (_) { return fallback; }
    };
    setDraftId(r.id);
    setMilestoneName(r.milestone_name || '');
    setMilestoneOverview(r.milestone_overview || '');
    setScopeOfWork(r.scope_of_work || '');
    setDeliverables((r.deliverables || []).map((d) => ({
      name: d.deliverable_name || d.name || '',
      type: d.deliverable_type || d.type || 'physical',
      description: d.description || '',
      acceptanceCriteria: d.acceptance_criteria || d.acceptanceCriteria || '',
      qualityStandard: d.quality_standard || d.qualityStandard || '',
      aiSuggested: Boolean(d.ai_suggested ?? d.aiSuggested),
    })));
    setExclusions(parse(r.exclusions, []));
    setAcceptanceCriteria(r.acceptance_criteria || '');
    setDependencies(r.dependencies || '');
    setEstimatedDuration(r.estimated_duration ? String(r.estimated_duration) : '');
    setDurationUnit(r.duration_unit || 'weeks');
    setReferencePhotos(parse(r.reference_photos, []));
    setReferenceDocuments(parse(r.reference_documents, []));
    setLaborCost(r.labor_cost != null ? String(r.labor_cost) : '');
    setMaterialsCost(r.materials_cost != null ? String(r.materials_cost) : '');
    setMaterialCostCoverage(r.material_cost_coverage || '');
    setMaterialCostCoverageDetails(r.material_cost_coverage_details || '');
    setOtherCharges(parse(r.other_charges, []) || []);
    setCurrency(r.currency || 'USD');
    setPaymentTerms(r.payment_terms || '');
    const fh = parse(r.final_handover, null);
    if (fh) {
      setHandoverSummary(fh.summary || '');
      setCertificates(fh.certificates || []);
      setPunchListItems(fh.punchListItems || []);
      setContractorSignoff(Boolean(fh.contractorSignoff));
    }
    setActiveStep(0);
    setShowReportGuide(false);
  }, [open, initialReport?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [deletingDraft, setDeletingDraft] = useState(false);
  const handleDeleteDraft = async () => {
    if (!draftId) return;
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    setDeletingDraft(true);
    try {
      await getApiClient().delete(`/milestone-reports/${draftId}`);
      showSnackbar('Draft deleted.', 'success');
      if (onDraftDeleted) onDraftDeleted(draftId);
      handleResetAndClose();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Could not delete the draft.', 'error');
    } finally {
      setDeletingDraft(false);
    }
  };

  // Auto-save draft
  useEffect(() => {
    if (milestoneName && open) {
      const timer = setTimeout(() => {
        saveDraft();
      }, 5000); // Auto-save every 5 seconds
      return () => clearTimeout(timer);
    }
  }, [milestoneName, milestoneOverview, scopeOfWork, deliverables, exclusions, acceptanceCriteria]);

  // Generate complete report with AI
  const handleGenerateWithAI = async () => {
    if (!milestoneName.trim()) {
      showSnackbar('Please enter a stage name first', 'warning');
      return;
    }

    setGenerating(true);
    const timeoutId = setTimeout(() => {
      setGenerating(false);
      showSnackbar('Request timeout. AI is taking longer than expected. Please try again.', 'error');
    }, 120000); // 120 second timeout

    try {
      const apiClient = getApiClient();
      
      showSnackbar('Generating completion report with AI... This may take 30-60 seconds.', 'info');
      
      const response = await apiClient.post('/ai/generate-milestone-report', {
        milestoneName,
        projectContext: {
          projectName: projectContext.name,
          projectType: projectContext.type,
          location: projectContext.location,
          description: projectContext.description,
        },
        referencePhotos: referencePhotos.map(p => p.url),
      });

      clearTimeout(timeoutId);

      if (!response.data || !response.data.data) {
        throw new Error('Invalid response from server');
      }

      const generated = response.data.data;
      
      // Populate all fields with AI-generated content
      setMilestoneOverview(generated.milestoneOverview || '');
      setScopeOfWork(generated.scopeOfWork || '');
      setDeliverables(generated.deliverables || []);
      setExclusions(generated.exclusions || []);
      setAcceptanceCriteria(generated.acceptanceCriteria || '');
      setDependencies(generated.dependencies || '');
      setEstimatedDuration(generated.estimatedDuration || '');
      setDurationUnit(generated.durationUnit || 'weeks');

      // Move to next step
      setActiveStep(1);
      
      showSnackbar('Completion report generated. Review and edit as needed.', 'success');
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error generating report:', error);
      
      let errorMessage = 'Failed to generate report';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Request timed out. The AI took too long to respond. Please try again.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showSnackbar(`Error: ${errorMessage}`, 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Improve specific section with AI
  const handleImproveSection = async (section, currentContent, setter) => {
    if (!currentContent.trim()) {
      showSnackbar('Please enter some content first', 'warning');
      return;
    }

    setLoading(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post('/ai/improve-milestone-section', {
        section,
        currentContent,
        context: {
          milestoneName,
          projectType: projectContext.type,
        },
      });

      setter(response.data.data.improvedContent);
      showSnackbar('✨ Content improved!', 'success');
    } catch (error) {
      console.error('Error improving section:', error);
      showSnackbar('Failed to improve content. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Suggest deliverables with AI
  const handleSuggestDeliverables = async () => {
    if (!scopeOfWork.trim()) {
      showSnackbar('Please enter scope of work first', 'warning');
      return;
    }

    setLoading(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post('/ai/suggest-deliverables', {
        milestoneName,
        scopeOfWork,
        projectType: projectContext.type,
      });

      const suggested = response.data.data;

      // Handle different response formats - now expecting array directly from backend
      let newDeliverables = [];
      if (Array.isArray(suggested)) {
        newDeliverables = suggested;
      } else if (suggested.deliverables && Array.isArray(suggested.deliverables)) {
        newDeliverables = suggested.deliverables;
      } else if (typeof suggested === 'object') {
        // Fallback: try to extract any array from the object
        const possibleArray = Object.values(suggested).find(val => Array.isArray(val));
        if (possibleArray) {
          newDeliverables = possibleArray;
        }
      }

      // Validate and normalize deliverables
      newDeliverables = newDeliverables.filter(d => d && d.name && d.description);

      // Add to existing deliverables instead of replacing
      if (newDeliverables.length > 0) {
        setDeliverables([...deliverables, ...newDeliverables]);
        showSnackbar(`✨ Added ${newDeliverables.length} AI-suggested deliverable${newDeliverables.length > 1 ? 's' : ''}!`, 'success');
      } else {
        showSnackbar('No deliverables were generated. Try providing more detail in the scope of work.', 'warning');
      }
    } catch (error) {
      console.error('Error suggesting deliverables:', error);
      showSnackbar('Failed to suggest deliverables. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Improve deliverables with AI
  const handleImproveDeliverables = async () => {
    if (deliverables.length === 0) {
      showSnackbar('Please add some deliverables first', 'warning');
      return;
    }

    setLoading(true);
    try {
      const apiClient = getApiClient();

      // Use the suggest-deliverables endpoint with current context to get enhanced versions
      const response = await apiClient.post('/ai/suggest-deliverables', {
        milestoneName,
        scopeOfWork: scopeOfWork + '\n\nCurrent deliverables for enhancement:\n' +
          deliverables.map(d => `- ${d.name}: ${d.description}`).join('\n'),
        projectType: projectContext.type,
      });

      const suggested = response.data.data;

      // Handle response format
      let improvedDeliverables = [];
      if (Array.isArray(suggested)) {
        improvedDeliverables = suggested;
      } else if (suggested.deliverables && Array.isArray(suggested.deliverables)) {
        improvedDeliverables = suggested.deliverables;
      }

      if (improvedDeliverables.length > 0) {
        // Merge with existing, keeping user-added fields
        const enhanced = improvedDeliverables.map((improved, index) => {
          const existing = deliverables[index] || {};
          return {
            ...existing,
            ...improved,
            name: improved.name || existing.name,
            description: improved.description || existing.description,
            acceptanceCriteria: improved.acceptanceCriteria || existing.acceptanceCriteria,
            qualityStandard: improved.qualityStandard || existing.qualityStandard || '',
            aiSuggested: true,
          };
        });

        setDeliverables(enhanced);
        showSnackbar('✨ Deliverables improved with enhanced details!', 'success');
      } else {
        showSnackbar('Unable to improve deliverables. Please try again.', 'warning');
      }
    } catch (error) {
      console.error('Error improving deliverables:', error);
      showSnackbar('Failed to improve deliverables. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Save draft
  const saveDraft = async () => {
    if (!milestoneName.trim()) return;

    try {
      // Calculate total cost for draft
      const laborCostNum = parseFloat(laborCost) || 0;
      const materialsCostNum = parseFloat(materialsCost) || 0;
      const otherChargesTotal = otherCharges.reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);
      const totalCost = laborCostNum + materialsCostNum + otherChargesTotal;

      const reportData = {
        projectId,
        milestoneName,
        milestoneOverview,
        scopeOfWork,
        exclusions,
        acceptanceCriteria,
        dependencies,
        estimatedDuration: parseInt(estimatedDuration) || null,
        durationUnit,
        referencePhotos,
        referenceDocuments,
        laborCost: laborCostNum || null,
        materialsCost: materialsCostNum || null,
        materialCostCoverage: materialCostCoverage || null,
        materialCostCoverageDetails: materialCostCoverageDetails || null,
        otherCharges: otherCharges.length > 0 ? otherCharges : null,
        totalCost: totalCost || null,
        currency,
        paymentTerms: paymentTerms || null,
        reportType: reportMode,
        finalHandover: isFinalHandover ? {
          summary: handoverSummary,
          certificates,
          punchListItems,
          contractorSignoff,
        } : null,
        aiGenerated: true,
      };

      const apiClient = getApiClient();
      let reportId = draftId;
      if (reportId) {
        try {
          await apiClient.put(`/milestone-reports/${reportId}`, reportData);
        } catch (err) {
          // Draft gone (deleted elsewhere) — fall through and create a fresh one.
          if (err.response?.status !== 404) throw err;
          reportId = null;
        }
      }
      if (!reportId) {
        const response = await apiClient.post('/milestone-reports', reportData);
        reportId = response.data.data.id;
        setDraftId(reportId);
      }
      
      // Save deliverables
      if (deliverables.length > 0) {
        await apiClient.post(`/milestone-reports/${reportId}/deliverables`, {
          deliverables: deliverables.map((d, index) => ({
            deliverableName: d.name,
            deliverableType: d.type || 'physical',
            description: d.description,
            acceptanceCriteria: d.acceptanceCriteria,
            qualityStandard: d.qualityStandard || null,
            sequenceOrder: index + 1,
            aiSuggested: d.aiSuggested || false,
          })),
        });
      }

      setSavedDraft(true);
      setTimeout(() => setSavedDraft(false), 3000);
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  };

  // Submit report to client
  const handleSubmitReport = async () => {
    console.log('🎯 handleSubmitReport called');
    console.log('Current state:', {
      milestoneName: milestoneName?.trim(),
      scopeOfWork: scopeOfWork?.trim(),
      deliverables: deliverables?.length,
      laborCost,
      materialCostCoverage,
      projectId,
    });

    if (!milestoneName.trim()) {
      showSnackbar('Please enter a stage name', 'warning');
      return;
    }

    if (!scopeOfWork.trim()) {
      showSnackbar('Please provide a scope of work', 'warning');
      return;
    }

    if (deliverables.length === 0) {
      showSnackbar('Please add at least one deliverable', 'warning');
      return;
    }

    // Validate mandatory cost fields
    if (!laborCost || parseFloat(laborCost) <= 0) {
      showSnackbar('Please enter the labor cost you should be paid for this stage', 'warning');
      setActiveStep(5); // Go to Payment & Costs step
      return;
    }

    if (!materialCostCoverage) {
      showSnackbar('Please specify who will cover the material costs', 'warning');
      setActiveStep(5); // Go to Payment & Costs step
      return;
    }

    if (isFinalHandover && !contractorSignoff) {
      showSnackbar('Please confirm the final handover sign-off before submitting', 'warning');
      setActiveStep(6);
      return;
    }

    if (!projectId) {
      console.error('ERROR: projectId is not defined', { projectId });
      showSnackbar('Error: Project ID is missing. Please reload the page.', 'error');
      return;
    }

    console.log('✅ All validations passed, proceeding with submission...');
    setLoading(true);
    try {
      // Calculate total cost
      const laborCostNum = parseFloat(laborCost) || 0;
      const materialsCostNum = parseFloat(materialsCost) || 0;
      const otherChargesTotal = otherCharges.reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);
      const totalCost = laborCostNum + materialsCostNum + otherChargesTotal;

      // Create report
      const reportData = {
        projectId,
        milestoneName,
        milestoneOverview,
        scopeOfWork,
        exclusions,
        acceptanceCriteria,
        dependencies,
        estimatedDuration: parseInt(estimatedDuration) || null,
        durationUnit,
        referencePhotos,
        referenceDocuments,
        laborCost: laborCostNum,
        materialsCost: materialsCostNum,
        materialCostCoverage,
        materialCostCoverageDetails,
        otherCharges: otherCharges.length > 0 ? otherCharges : null,
        totalCost,
        currency,
        paymentTerms,
        reportType: reportMode,
        finalHandover: isFinalHandover ? {
          summary: handoverSummary,
          certificates,
          punchListItems,
          contractorSignoff,
        } : null,
        aiGenerated: true,
      };

      console.log('📤 Submitting report with data:', reportData);

      const apiClient = getApiClient();
      
      console.log('🔄 Step 1: Creating milestone report...');
      let reportResponse = null;
      let reportId = draftId;
      if (reportId) {
        try {
          reportResponse = await apiClient.put(`/milestone-reports/${reportId}`, reportData);
        } catch (err) {
          if (err.response?.status !== 404) throw err;
          reportId = null;
        }
      }
      if (!reportId) {
        reportResponse = await apiClient.post('/milestone-reports', reportData);
        reportId = reportResponse.data.data.id;
        setDraftId(reportId);
      }

      console.log('✅ Step 1 Complete: Report created with ID:', reportId);

      // Add deliverables
      console.log('🔄 Step 2: Adding deliverables...');
      await apiClient.post(`/milestone-reports/${reportId}/deliverables`, {
        deliverables: deliverables.map((d, index) => ({
          deliverableName: d.name,
          deliverableType: d.type || 'physical', // Changed from 'tangible' to match DB constraint
          description: d.description,
          acceptanceCriteria: d.acceptanceCriteria,
          qualityStandard: d.qualityStandard || null,
          sequenceOrder: index + 1,
          aiSuggested: d.aiSuggested || false,
        })),
      });

      console.log('✅ Step 2 Complete: Deliverables added');

      // Submit to client
      console.log('🔄 Step 3: Submitting to client...');
      await apiClient.post(`/milestone-reports/${reportId}/submit`);

      console.log('✅ Step 3 Complete: Report submitted to client');

      showSnackbar('Completion report submitted to the owner for approval.', 'success');

      if (onReportCreated) {
        onReportCreated({ ...(reportResponse?.data?.data || {}), id: reportId, status: 'submitted' });
      }

      // Close preview and main dialog
      setShowPreview(false);
      handleClose();
    } catch (error) {
      console.error('Error submitting report:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        stack: error.stack,
      });
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit report. Please try again.';
      showSnackbar(`Error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Attachments go to S3 straight away; the report stores the real URL.
  // (A browser blob URL used to be stored — the owner could never open it.)
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const uploadFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return [];
    setUploadingFiles(true);
    try {
      return await milestoneService.uploadReportFiles(files);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Upload failed. Please try again.', 'error');
      return [];
    } finally {
      setUploadingFiles(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const uploaded = await uploadFiles(event);
    if (!uploaded.length) return;
    setReferencePhotos((prev) => [...prev, ...uploaded.map((f) => ({ url: f.url, name: f.name, caption: '' }))]);
  };

  const handleDocumentUpload = async (event) => {
    const uploaded = await uploadFiles(event);
    if (!uploaded.length) return;
    setReferenceDocuments((prev) => [...prev, ...uploaded.map((f) => ({
      url: f.url, name: f.name, type: f.type || 'document', size: f.size, note: '',
    }))]);
  };

  // Add exclusion
  const handleAddExclusion = () => {
    if (newExclusion.trim()) {
      setExclusions([...exclusions, newExclusion.trim()]);
      setNewExclusion('');
    }
  };

  // Remove exclusion
  const handleRemoveExclusion = (index) => {
    setExclusions(exclusions.filter((_, i) => i !== index));
  };

  // Add/Edit deliverable
  const handleSaveDeliverable = (deliverable) => {
    if (editingDeliverable !== null) {
      const updated = [...deliverables];
      updated[editingDeliverable] = deliverable;
      setDeliverables(updated);
    } else {
      setDeliverables([...deliverables, deliverable]);
    }
    setEditingDeliverable(null);
  };

  // Remove deliverable
  const handleRemoveDeliverable = (index) => {
    setDeliverables(deliverables.filter((_, i) => i !== index));
  };

  // Handle edits from Preview Modal
  const handlePreviewEdit = (section, value) => {
    switch(section) {
      case 'milestoneOverview': setMilestoneOverview(value); break;
      case 'scopeOfWork': setScopeOfWork(value); break;
      case 'acceptanceCriteria': setAcceptanceCriteria(value); break;
      default: break;
    }
  };

  // Navigation
  const handleNext = () => {
    // Step-specific validation
    if (activeStep === 0 && !milestoneName.trim()) {
      showSnackbar('Please enter a stage name', 'warning');
      return;
    }
    if (activeStep === 1 && !scopeOfWork.trim()) {
      showSnackbar('Please enter scope of work', 'warning');
      return;
    }
    if (activeStep === 2 && deliverables.length === 0) {
      showSnackbar('Please add at least one deliverable', 'warning');
      return;
    }
    if (activeStep === 5) {
      if (!laborCost || parseFloat(laborCost) <= 0) {
        showSnackbar('Please enter labor cost', 'warning');
        return;
      }
      if (!materialCostCoverage) {
        showSnackbar('Please specify material cost coverage', 'warning');
        return;
      }
    }

    if (activeStep === steps.length - 1) {
      // Review Step - Open Preview
      setShowPreview(true);
    } else {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleClose = () => {
    if (milestoneName.trim()) {
      setCloseDialogOpen(true);
    } else {
      handleResetAndClose();
    }
  };

  const handleCloseConfirm = () => {
    saveDraft();
    setCloseDialogOpen(false);
    handleResetAndClose();
  };

  const handleResetAndClose = () => {
    // Reset all state
    setDraftId(null);
    setActiveStep(0);
    setMilestoneName('');
    setMilestoneOverview('');
    setScopeOfWork('');
    setDeliverables([]);
    setExclusions([]);
    setAcceptanceCriteria('');
    setDependencies('');
    setEstimatedDuration('');
    setReferencePhotos([]);
    setReferenceDocuments([]);
    setLaborCost('');
    setMaterialsCost('');
    setMaterialCostCoverage('');
    setMaterialCostCoverageDetails('');
    setOtherCharges([]);
    setPaymentTerms('');
    setHandoverSummary('');
    setCertificates([]);
    setNewCertificate('');
    setPunchListItems([]);
    setNewPunchListItem('');
    setContractorSignoff(false);
    onClose();
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Stage Name
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Which stage are you reporting on?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select from the list or type a custom name. You can filter by stage name or category.
            </Typography>

            <Autocomplete
              freeSolo
              options={MILESTONE_OPTIONS}
              groupBy={(option) => option.group || ''}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.inputValue) return option.inputValue;
                return option.label;
              }}
              filterOptions={(options, params) => {
                const { inputValue } = params;
                if (!inputValue) return options;
                const query = inputValue.toLowerCase();
                const filtered = options.filter(
                  (option) =>
                    option.label.toLowerCase().includes(query) ||
                    option.group.toLowerCase().includes(query)
                );
                const hasExactMatch = options.some(
                  (opt) => opt.label.toLowerCase() === query
                );
                if (!hasExactMatch) {
                  filtered.push({
                    inputValue,
                    label: `Use custom name: "${inputValue}"`,
                    group: '__custom__',
                  });
                }
                return filtered;
              }}
              inputValue={milestoneName}
              onInputChange={(event, newValue, reason) => {
                if (reason === 'input') {
                  setMilestoneName(newValue);
                }
              }}
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                  setMilestoneName(newValue);
                } else if (newValue?.inputValue) {
                  setMilestoneName(newValue.inputValue);
                } else if (newValue?.label) {
                  setMilestoneName(newValue.label);
                } else {
                  setMilestoneName('');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Stage Name"
                  placeholder="Type to search or enter a custom name..."
                  sx={{ mb: 3 }}
                  autoFocus
                />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  {params.group !== '__custom__' && (
                    <Box
                      sx={{
                        px: 2,
                        py: 0.5,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        color: 'primary.main',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        backgroundColor: 'grey.50',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        position: 'sticky',
                        top: -8,
                        zIndex: 1,
                      }}
                    >
                      {params.group}
                    </Box>
                  )}
                  <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>{params.children}</ul>
                </li>
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    fontStyle: option.inputValue ? 'italic' : 'normal',
                    color: option.inputValue ? 'text.secondary' : 'text.primary',
                    fontSize: '0.9rem',
                  }}
                >
                  {option.label}
                </Box>
              )}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
              onClick={handleGenerateWithAI}
              disabled={!milestoneName.trim() || generating}
              sx={{
                background: `linear-gradient(45deg, ${brand.primary} 30%, ${brand.primaryLight} 90%)`,
                color: 'white',
                py: 1.5,
                fontWeight: 'bold',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(45deg, #7a29c2 30%, #a66dd3 90%)',
                },
              }}
            >
              {generating ? 'Generating Complete Report...' : 'Generate Complete Report with AI'}
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
              AI will generate a professional completion report with all sections filled out. You can review and edit everything.
            </Typography>
          </Box>
        );

      case 1: // Overview & Scope
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Stage Overview & Scope
            </Typography>
            
            {/* Overview */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Stage Overview</Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                  onClick={() => handleImproveSection('overview', milestoneOverview, setMilestoneOverview)}
                  disabled={!milestoneOverview.trim() || loading}
                  sx={{
                    background: `linear-gradient(45deg, ${brand.primary} 30%, ${brand.primaryLight} 90%)`,
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? 'Improving...' : 'Improve with AI'}
                </Button>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={milestoneOverview}
                onChange={(e) => setMilestoneOverview(e.target.value)}
                placeholder="High-level description of this phase, objectives, and importance..."
              />
            </Box>

            {/* Scope of Work */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Scope of Work *</Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                  onClick={() => handleImproveSection('scope', scopeOfWork, setScopeOfWork)}
                  disabled={!scopeOfWork.trim() || loading}
                  sx={{
                    background: `linear-gradient(45deg, ${brand.primary} 30%, ${brand.primaryLight} 90%)`,
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? 'Improving...' : 'Improve with AI'}
                </Button>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={scopeOfWork}
                onChange={(e) => setScopeOfWork(e.target.value)}
                placeholder="Detailed description of all activities, materials, methods, and quality standards..."
                required
              />
            </Box>
          </Box>
        );

      case 2: // Deliverables
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Deliverables</Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                onClick={handleSuggestDeliverables}
                disabled={loading}
                sx={{
                  background: `linear-gradient(45deg, ${brand.primary} 30%, ${brand.primaryLight} 90%)`,
                  textTransform: 'none',
                  fontWeight: 'bold'
                }}
              >
                {loading ? 'Suggesting...' : 'Suggest with AI'}
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              What specific outputs will be provided when this stage is complete?
            </Typography>

            {deliverables.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Click "Suggest with AI" to get AI-generated deliverables, or add manually below.
              </Alert>
            ) : (
              <List sx={{ mb: 2 }}>
                {deliverables.map((deliverable, index) => (
                  <Card key={index} sx={{ mb: 1 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">{index + 1}. {deliverable.name}</Typography>
                            {deliverable.aiSuggested && (
                              <Chip label="AI" size="small" color="secondary" />
                            )}
                            <Chip label={deliverable.type || 'physical'} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {deliverable.description}
                          </Typography>
                          {deliverable.acceptanceCriteria && (
                            <Box sx={{ bgcolor: 'success.50', p: 1, borderRadius: 1, mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold" display="block">
                                Acceptance Criteria:
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                ✓ {deliverable.acceptanceCriteria}
                              </Typography>
                            </Box>
                          )}
                          {deliverable.qualityStandard && (
                            <Chip
                              label={`Quality: ${deliverable.qualityStandard}`}
                              size="small"
                              color="info"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveDeliverable(index)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </List>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setAddDeliverableDialogOpen(true)}
              >
                Add Deliverable Manually
              </Button>
              {deliverables.length > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                  onClick={handleImproveDeliverables}
                  disabled={loading}
                  sx={{
                    background: `linear-gradient(45deg, ${brand.primary} 30%, ${brand.primaryLight} 90%)`,
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? 'Improving...' : 'Improve with AI'}
                </Button>
              )}
            </Box>
          </Box>
        );

      case 3: // Exclusions & Criteria
        return (
          <Box>
            {/* Exclusions */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Exclusions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                What is NOT included in this stage? (Prevents scope creep)
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={newExclusion}
                  onChange={(e) => setNewExclusion(e.target.value)}
                  placeholder="e.g., Underground utilities installation"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddExclusion();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddExclusion}
                  disabled={!newExclusion.trim()}
                >
                  Add
                </Button>
              </Box>

              {exclusions.length > 0 && (
                <List dense>
                  {exclusions.map((exclusion, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={`• ${exclusion}`} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemoveExclusion(index)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {/* Acceptance Criteria */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Acceptance Criteria</Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                  onClick={() => handleImproveSection('acceptance', acceptanceCriteria, setAcceptanceCriteria)}
                  disabled={!acceptanceCriteria.trim() || loading}
                  sx={{
                    background: `linear-gradient(45deg, ${brand.primary} 30%, ${brand.primaryLight} 90%)`,
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? 'Improving...' : 'Improve with AI'}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How will you know this stage is complete? What defines "done"?
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={acceptanceCriteria}
                onChange={(e) => setAcceptanceCriteria(e.target.value)}
                placeholder="Measurable completion criteria, quality standards, required inspections..."
              />
            </Box>

            {/* Dependencies */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Dependencies (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={dependencies}
                onChange={(e) => setDependencies(e.target.value)}
                placeholder="Prerequisites that must be completed first, required approvals or permits..."
              />
            </Box>
          </Box>
        );

      case 4: // Timeline & Evidence
        return (
          <Box>
            {/* Timeline */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Estimated Timeline
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Duration"
                    value={estimatedDuration}
                    onChange={(e) => setEstimatedDuration(e.target.value)}
                    placeholder="e.g., 3"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    select
                    label="Unit"
                    value={durationUnit}
                    onChange={(e) => setDurationUnit(e.target.value)}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            {/* Reference Photos */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Completion Photos (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload photos that prove the stage is complete or show the final condition for owner review
              </Typography>

              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                sx={{ mb: 2 }}
              >
                {uploadingFiles ? 'Uploading…' : 'Upload Photos'}
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  disabled={uploadingFiles}
                  onChange={handlePhotoUpload}
                />
              </Button>

              {referencePhotos.length > 0 && (
                <Grid container spacing={2}>
                  {referencePhotos.map((photo, index) => (
                    <Grid item xs={6} sm={4} key={index}>
                      <Card>
                        <Box
                          component="img"
                          src={photo.url}
                          sx={{
                            width: '100%',
                            height: 150,
                            objectFit: 'cover',
                          }}
                        />
                        <CardContent>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Add caption..."
                            value={photo.caption}
                            onChange={(e) => {
                              const updated = [...referencePhotos];
                              updated[index].caption = e.target.value;
                              setReferencePhotos(updated);
                            }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Completion Documents (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload the plans, 3D designs, receipts, certificates, inspection notes, warranties, signed checklists, or other documents requested for this stage. PDF, Word, Excel and design files (SKP, DWG, DXF, RVT, OBJ, STL, GLB, IFC…) all work.
              </Typography>

              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                sx={{ mb: 2 }}
              >
                {uploadingFiles ? 'Uploading…' : 'Upload Documents'}
                <input
                  type="file"
                  hidden
                  multiple
                  accept={REPORT_DOCUMENT_ACCEPT}
                  disabled={uploadingFiles}
                  onChange={handleDocumentUpload}
                />
              </Button>

              {referenceDocuments.length > 0 && (
                <Grid container spacing={2}>
                  {referenceDocuments.map((document, index) => (
                    <Grid item xs={12} sm={6} key={`${document.name}-${index}`}>
                      <Card variant="outlined">
                        <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <Description color="primary" />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" noWrap component="a" href={document.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', color: 'primary.main', textDecoration: 'none' }}>
                              {document.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {document.type || 'Document'}
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Add note..."
                              value={document.note}
                              onChange={(e) => {
                                const updated = [...referenceDocuments];
                                updated[index].note = e.target.value;
                                setReferenceDocuments(updated);
                              }}
                              sx={{ mt: 1 }}
                            />
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setReferenceDocuments(referenceDocuments.filter((_, i) => i !== index))}
                            aria-label="Remove document"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Box>
        );

      case 5: // Payment & Costs
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Payment & Costs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Specify payment details and who covers material costs
            </Typography>

            {/* Labor Cost - Mandatory */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Labor Cost <Chip label="Required" color="error" size="small" sx={{ ml: 1 }} />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                How much should you be paid for this stage?
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Labor Cost"
                value={laborCost}
                onChange={(e) => setLaborCost(e.target.value)}
                placeholder="0.00"
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
                }}
                error={!laborCost || parseFloat(laborCost) <= 0}
                helperText={!laborCost || parseFloat(laborCost) <= 0 ? 'Labor cost is required' : ''}
              />
            </Box>

            {/* Materials Cost */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Materials Cost
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Total cost of materials required for this stage
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Materials Cost"
                value={materialsCost}
                onChange={(e) => setMaterialsCost(e.target.value)}
                placeholder="0.00"
                InputProps={{
                  startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
                }}
              />
            </Box>

            {/* Material Cost Coverage - Mandatory */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Material Cost Coverage <Chip label="Required" color="error" size="small" sx={{ ml: 1 }} />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Who will cover the material costs?
              </Typography>
              <FormControl fullWidth>
                <RadioGroup
                  value={materialCostCoverage}
                  onChange={(e) => setMaterialCostCoverage(e.target.value)}
                >
                  <FormControlLabel value="contractor" control={<Radio />} label="Contractor covers materials" />
                  <FormControlLabel value="client" control={<Radio />} label="Client covers materials" />
                  <FormControlLabel value="shared" control={<Radio />} label="Shared (specify split below)" />
                </RadioGroup>
              </FormControl>
              {materialCostCoverage && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Material Cost Coverage Details"
                  value={materialCostCoverageDetails}
                  onChange={(e) => setMaterialCostCoverageDetails(e.target.value)}
                  placeholder={
                    materialCostCoverage === 'contractor' 
                      ? 'Contractor will purchase and provide all materials...'
                      : materialCostCoverage === 'client'
                      ? 'Client will provide materials or reimburse contractor...'
                      : 'Specify the cost split (e.g., Client covers 60%, Contractor covers 40%)...'
                  }
                  sx={{ mt: 2 }}
                />
              )}
            </Box>

            {/* Other Charges */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Other Charges (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add any additional charges (equipment rental, permits, etc.)
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  label="Description"
                  value={newOtherCharge.description}
                  onChange={(e) => setNewOtherCharge({ ...newOtherCharge, description: e.target.value })}
                  sx={{ flex: 2 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Amount"
                  value={newOtherCharge.amount}
                  onChange={(e) => setNewOtherCharge({ ...newOtherCharge, amount: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
                  }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (newOtherCharge.description && newOtherCharge.amount) {
                      setOtherCharges([...otherCharges, { ...newOtherCharge }]);
                      setNewOtherCharge({ description: '', amount: '', category: 'other' });
                    }
                  }}
                  disabled={!newOtherCharge.description || !newOtherCharge.amount}
                >
                  Add
                </Button>
              </Box>

              {otherCharges.length > 0 && (
                <List dense>
                  {otherCharges.map((charge, index) => (
                    <ListItem key={index}>
                      <ListItemText 
                        primary={charge.description}
                        secondary={`${currency} ${parseFloat(charge.amount || 0).toFixed(2)}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setOtherCharges(otherCharges.filter((_, i) => i !== index))}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {/* Payment Terms */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Payment Terms (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Payment schedule and terms (e.g., 50% upfront, 50% on completion)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="e.g., 50% payment upon approval, 50% upon completion and verification"
              />
            </Box>

            {/* Cost Summary */}
            <Paper sx={{ p: 3, mt: 3, bgcolor: 'primary.50', textAlign: 'right' }}>
              <Typography variant="h6" gutterBottom>Total Stage Cost</Typography>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {currency} {(
                  parseFloat(laborCost || 0) +
                  parseFloat(materialsCost || 0) +
                  otherCharges.reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0)
                ).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Labor Cost:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {currency} {parseFloat(laborCost || 0).toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Materials Cost:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {currency} {parseFloat(materialsCost || 0).toFixed(2)}
                </Typography>
              </Box>
              {otherCharges.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Other Charges:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {currency} {otherCharges.reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        );

      case 6: // Final Handover or Review & Submit
        if (isFinalHandover) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Final Handover
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Summarize the completed project, list handover documents, close out any punch-list items, and sign off before requesting final payment release.
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Final handover summary"
                value={handoverSummary}
                onChange={(e) => setHandoverSummary(e.target.value)}
                placeholder="Summarize the completed work, final condition, and anything the owner should know at handover..."
                sx={{ mb: 3 }}
              />

              <Typography variant="subtitle1" gutterBottom>
                Certificates, warranties, and handover documents
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  label="Document name"
                  value={newCertificate}
                  onChange={(e) => setNewCertificate(e.target.value)}
                  placeholder="e.g., Electrical compliance certificate"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (newCertificate.trim()) {
                      setCertificates([...certificates, newCertificate.trim()]);
                      setNewCertificate('');
                    }
                  }}
                  disabled={!newCertificate.trim()}
                >
                  Add
                </Button>
              </Box>
              {certificates.length > 0 && (
                <List dense sx={{ mb: 3 }}>
                  {certificates.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={item} />
                      <ListItemSecondaryAction>
                        <IconButton size="small" edge="end" onClick={() => setCertificates(certificates.filter((_, i) => i !== index))}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              <Typography variant="subtitle1" gutterBottom>
                Punch list / snag items
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  label="Punch-list item"
                  value={newPunchListItem}
                  onChange={(e) => setNewPunchListItem(e.target.value)}
                  placeholder="e.g., Touch up paint behind kitchen door"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (newPunchListItem.trim()) {
                      setPunchListItems([...punchListItems, { text: newPunchListItem.trim(), resolved: false }]);
                      setNewPunchListItem('');
                    }
                  }}
                  disabled={!newPunchListItem.trim()}
                >
                  Add
                </Button>
              </Box>
              {punchListItems.length > 0 && (
                <List dense sx={{ mb: 3 }}>
                  {punchListItems.map((item, index) => (
                    <ListItem key={index}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={item.resolved}
                            onChange={(e) => setPunchListItems(punchListItems.map((p, i) => i === index ? { ...p, resolved: e.target.checked } : p))}
                          />
                        }
                        label={item.text}
                      />
                      <ListItemSecondaryAction>
                        <IconButton size="small" edge="end" onClick={() => setPunchListItems(punchListItems.filter((_, i) => i !== index))}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              <Alert severity="info" sx={{ mb: 2 }}>
                The final report should include completed work, final photos, certificates, warranties, remaining snag items, and the final payment release request.
              </Alert>

              <FormControlLabel
                control={<Checkbox checked={contractorSignoff} onChange={(e) => setContractorSignoff(e.target.checked)} />}
                label="I confirm the work described in this final handover report is complete and ready for owner review."
              />
            </Box>
          );
        }

        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Description sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>Ready for Final Review</Typography>
            <Typography color="text.secondary" paragraph>
              Your completion report data has been collected. Click "Preview Report" to view the professional document,
              make final edits, and submit.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
              <Button variant="contained" size="large" onClick={() => setShowPreview(true)} startIcon={<Description />}>
                Preview Report
              </Button>
              <Button 
                variant="outlined" 
                size="large" 
                onClick={() => setActiveStep(1)} 
                startIcon={<Edit />}
              >
                Edit
              </Button>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
              Quick Summary
            </Typography>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>{milestoneName}</Typography>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>Overview</Typography>
              <Typography variant="body2" paragraph>{milestoneOverview || 'Not provided'}</Typography>
              
              <Typography variant="subtitle2" gutterBottom>Scope of Work</Typography>
              <Typography variant="body2" paragraph>{scopeOfWork || 'Not provided'}</Typography>
              
              <Typography variant="subtitle2" gutterBottom>Deliverables ({deliverables.length})</Typography>
              <List dense>
                {deliverables.map((d, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={`${i + 1}. ${d.name}`} secondary={d.description} />
                  </ListItem>
                ))}
              </List>
              
              {exclusions.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Exclusions</Typography>
                  <List dense>
                    {exclusions.map((e, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={`• ${e}`} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
              
              <Typography variant="subtitle2" gutterBottom>Acceptance Criteria</Typography>
              <Typography variant="body2" paragraph>{acceptanceCriteria || 'Not provided'}</Typography>
              
              {estimatedDuration && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Timeline</Typography>
                  <Typography variant="body2">{estimatedDuration} {durationUnit}</Typography>
                </>
              )}
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Payment & Costs</Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2"><strong>Labor Cost:</strong> {currency} {parseFloat(laborCost || 0).toFixed(2)}</Typography>
                <Typography variant="body2"><strong>Materials Cost:</strong> {currency} {parseFloat(materialsCost || 0).toFixed(2)}</Typography>
                {otherCharges.length > 0 && (
                  <Typography variant="body2"><strong>Other Charges:</strong> {currency} {otherCharges.reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0).toFixed(2)}</Typography>
                )}
                <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                  <strong>Total Cost:</strong> {currency} {(
                    parseFloat(laborCost || 0) + 
                    parseFloat(materialsCost || 0) + 
                    otherCharges.reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0)
                  ).toFixed(2)}
                </Typography>
                {materialCostCoverage && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Material Coverage:</strong> {
                      materialCostCoverage === 'contractor' ? 'Contractor covers materials' :
                      materialCostCoverage === 'client' ? 'Client covers materials' :
                      'Shared coverage'
                    }
                  </Typography>
                )}
                {materialCostCoverageDetails && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    {materialCostCoverageDetails}
                  </Typography>
                )}
                {paymentTerms && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Payment Terms:</strong> {paymentTerms}
                  </Typography>
                )}
              </Box>
            </Paper>

            <Alert severity="info" sx={{ mb: 2 }}>
              Once submitted, the client will review this report and can approve, request changes, or reject it. 
              After approval, this stage is recorded against the project and the approved amount can move through the payment-release flow.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
        },
      }}
    >
      <DialogCloseButton onClick={handleClose} sx={{ color: 'white' }} />
      <DialogTitle
        sx={{
          background: brand.gradient,
          color: 'white',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AutoAwesome sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                {isFinalHandover ? 'Create Final Handover Report' : 'Create Completion Report'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {isFinalHandover ? 'Final sign-off, documents, and release request' : 'AI-Powered Stage Reporting'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {savedDraft && (
              <Chip 
                label="Draft Saved" 
                size="small" 
                sx={{ 
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                }}
                icon={<CheckCircle sx={{ color: 'white !important' }} />} 
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {/* Stepper */}
        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 4,
            '& .MuiStepLabel-root .Mui-completed': {
              color: brand.primary,
            },
            '& .MuiStepLabel-root .Mui-active': {
              color: brand.primary,
            },
            '& .MuiStepIcon-root.Mui-active': {
              color: brand.primary,
            },
            '& .MuiStepIcon-root.Mui-completed': {
              color: brand.primary,
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* First-timer guidance */}
        {showReportGuide && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            onClose={() => {
              setShowReportGuide(false);
              localStorage.setItem('eqaya_contractor_milestone_report_guide_seen', 'true');
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              How Stage Completion Reports Work
            </Typography>
            <Typography variant="body2" component="div">
              This tool helps you create a professional stage completion report in 7 steps:
              <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li><strong>Name &amp; describe</strong> the stage and its scope of work</li>
                <li><strong>List deliverables</strong> — AI enhances each one with professional detail</li>
                <li><strong>Upload photos</strong> as evidence of completed work</li>
                <li><strong>Set costs &amp; review</strong> — preview the full report, then submit to the client</li>
              </ol>
            </Typography>
          </Alert>
        )}

        {/* Step Content */}
        {renderStepContent()}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
          {draftId && (
            <Button
              color="error"
              onClick={handleDeleteDraft}
              disabled={deletingDraft || loading}
              startIcon={deletingDraft ? <CircularProgress size={18} /> : <Delete />}
            >
              Delete draft
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmitReport}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Send />}
              sx={{
                background: brand.gradient,
                color: 'white',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: brand.gradientHover,
                },
              }}
            >
              {loading ? 'Submitting...' : 'Submit to Client'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForward />}
              sx={{
                background: brand.gradient,
                color: 'white',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: brand.gradientHover,
                },
              }}
            >
              {activeStep === steps.length - 1 ? 'Preview' : 'Next'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>

    {/* Full Screen Preview Modal */}
    {showPreview && (
      <MilestoneReportDocumentPreview
        open={showPreview}
        onClose={() => setShowPreview(false)}
        onSubmit={handleSubmitReport}
        loading={loading}
        contractor={user}
        data={{
          milestoneName,
          milestoneOverview,
          scopeOfWork,
          deliverables,
          exclusions,
          acceptanceCriteria,
          dependencies,
          estimatedDuration,
          durationUnit,
          referencePhotos,
          referenceDocuments,
          laborCost,
          materialsCost,
          materialCostCoverage,
          materialCostCoverageDetails,
          otherCharges,
          currency,
          paymentTerms,
          reportMode,
          finalHandover: {
            summary: handoverSummary,
            certificates,
            punchListItems,
            contractorSignoff,
          },
        }}
        onEditSection={handlePreviewEdit}
      />
    )}

    {/* Close Confirmation Dialog */}
    <Dialog
      open={closeDialogOpen}
      onClose={() => setCloseDialogOpen(false)}
    >
      <DialogCloseButton onClick={() => setCloseDialogOpen(false)} />
      <DialogTitle>Close Completion Report?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to close? Your draft will be saved automatically.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleCloseConfirm} color="primary" variant="contained">Close & Save</Button>
      </DialogActions>
    </Dialog>

    {/* Snackbar for notifications */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={handleSnackbarClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
        {snackbar.message}
      </Alert>
    </Snackbar>

    {/* Add Deliverable Dialog */}
    <Dialog
      open={addDeliverableDialogOpen}
      onClose={() => {
        setAddDeliverableDialogOpen(false);
        setNewDeliverable({
          name: '',
          type: 'physical',
          description: '',
          acceptanceCriteria: '',
          qualityStandard: '',
        });
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogCloseButton onClick={() => {
        setAddDeliverableDialogOpen(false);
        setNewDeliverable({
          name: '',
          type: 'physical',
          description: '',
          acceptanceCriteria: '',
          qualityStandard: '',
        });
      }} />
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Add Deliverable</Typography>
          <Tooltip title="AI will enhance your deliverable with professional details">
            <Chip
              icon={<AutoAwesome />}
              label="AI Available"
              size="small"
              color="secondary"
              variant="outlined"
            />
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <Alert severity="info" sx={{ mb: 1 }}>
            Enter basic information and click "Improve with AI" to automatically fill in professional details.
          </Alert>

          <TextField
            label="Deliverable Name *"
            value={newDeliverable.name}
            onChange={(e) => setNewDeliverable({ ...newDeliverable, name: e.target.value })}
            placeholder="e.g., Foundation Concrete Pour"
            autoFocus
            fullWidth
            helperText="Brief, specific name for this deliverable"
          />

          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={newDeliverable.type}
              label="Type"
              onChange={(e) => setNewDeliverable({ ...newDeliverable, type: e.target.value })}
            >
              <MenuItem value="physical">Physical Output</MenuItem>
              <MenuItem value="document">Document/Report</MenuItem>
              <MenuItem value="media">Media/Photos</MenuItem>
              <MenuItem value="inspection">Inspection/Verification</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Description *</Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                onClick={async () => {
                  if (!newDeliverable.name.trim()) {
                    showSnackbar('Please enter a deliverable name first', 'warning');
                    return;
                  }

                  setLoading(true);
                  try {
                    const apiClient = getApiClient();
                    const response = await apiClient.post('/ai/suggest-deliverables', {
                      milestoneName,
                      scopeOfWork: `${scopeOfWork}\n\nGenerate a single detailed deliverable for: ${newDeliverable.name}`,
                      projectType: projectContext.type,
                    });

                    const suggested = response.data.data;
                    let enhanced = null;

                    if (Array.isArray(suggested) && suggested.length > 0) {
                      enhanced = suggested[0];
                    } else if (suggested.deliverables && Array.isArray(suggested.deliverables) && suggested.deliverables.length > 0) {
                      enhanced = suggested.deliverables[0];
                    }

                    if (enhanced) {
                      setNewDeliverable({
                        ...newDeliverable,
                        description: enhanced.description || newDeliverable.description,
                        acceptanceCriteria: enhanced.acceptanceCriteria || newDeliverable.acceptanceCriteria,
                        qualityStandard: enhanced.qualityStandard || newDeliverable.qualityStandard,
                      });
                      showSnackbar('✨ Deliverable enhanced with AI!', 'success');
                    } else {
                      showSnackbar('Could not enhance deliverable. Please enter details manually.', 'warning');
                    }
                  } catch (error) {
                    console.error('Error enhancing deliverable:', error);
                    showSnackbar('Failed to enhance deliverable. Please try again.', 'error');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={!newDeliverable.name.trim() || loading}
                sx={{
                  background: `linear-gradient(45deg, ${brand.primary} 30%, ${brand.primaryLight} 90%)`,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                }}
              >
                {loading ? 'Improving...' : 'Improve with AI'}
              </Button>
            </Box>
            <TextField
              value={newDeliverable.description}
              onChange={(e) => setNewDeliverable({ ...newDeliverable, description: e.target.value })}
              placeholder="What exactly will be delivered?"
              multiline
              rows={3}
              fullWidth
            />
          </Box>

          <TextField
            label="Acceptance Criteria *"
            value={newDeliverable.acceptanceCriteria}
            onChange={(e) => setNewDeliverable({ ...newDeliverable, acceptanceCriteria: e.target.value })}
            placeholder="How will you verify this is complete and meets requirements?"
            multiline
            rows={2}
            fullWidth
            helperText="Specific, measurable criteria for accepting this deliverable"
          />

          <TextField
            label="Quality Standard (Optional)"
            value={newDeliverable.qualityStandard}
            onChange={(e) => setNewDeliverable({ ...newDeliverable, qualityStandard: e.target.value })}
            placeholder="e.g., Per ACI 318 standards, ISO 9001 certified"
            fullWidth
            helperText="Industry standards or specifications that apply"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button
          onClick={() => {
            setAddDeliverableDialogOpen(false);
            setNewDeliverable({
              name: '',
              type: 'physical',
              description: '',
              acceptanceCriteria: '',
              qualityStandard: '',
            });
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (newDeliverable.name.trim() && newDeliverable.description.trim() && newDeliverable.acceptanceCriteria.trim()) {
              setDeliverables([...deliverables, { ...newDeliverable, aiSuggested: false }]);
              setAddDeliverableDialogOpen(false);
              setNewDeliverable({
                name: '',
                type: 'physical',
                description: '',
                acceptanceCriteria: '',
                qualityStandard: '',
              });
              showSnackbar('✅ Deliverable added successfully', 'success');
            } else {
              showSnackbar('Please fill in name, description, and acceptance criteria', 'warning');
            }
          }}
          disabled={!newDeliverable.name.trim() || !newDeliverable.description.trim() || !newDeliverable.acceptanceCriteria.trim()}
          sx={{
            background: brand.gradient,
            fontWeight: 600,
          }}
        >
          Add Deliverable
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AIMilestoneReportGenerator;
